/**
 * SignaturePad — dependency-free drawn e-signature.
 *
 * WHAT: a canvas the visitor signs with finger, stylus or mouse. Emits a
 * PNG data URL (`canvas.toDataURL('image/png')`) at the end of every
 * stroke and `null` when cleared, so the parent form can require a
 * signature before submit. Used by the open house CIS acknowledgment
 * step (N.J.A.C. 11:5-6.9 — the signed copy is emailed to the visitor).
 *
 * WHY no library: the kiosk runs on an iPad in Guided Access and on
 * visitors' phones scanned from a printed QR; a small pointer-events
 * canvas avoids a dependency and keeps working offline in the PWA shell.
 *
 * HOW: pointer events (touch / mouse / pen unified) with pointer capture
 * so a stroke that leaves the canvas still ends cleanly; the backing
 * store is scaled by devicePixelRatio for crisp lines; finished strokes
 * are kept in state so a resize (rotation, split view) replays them
 * instead of wiping the signature. Not a form control, so browser
 * autofill never touches it.
 *
 * Usage:
 *   <SignaturePad value={signature} onChange={setSignature} />
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Eraser } from 'lucide-react';

export interface SignaturePadProps {
    /** Current PNG data URL (controlled). `null` / '' clears the pad. */
    value?: string | null;
    onChange: (dataUrl: string | null) => void;
    disabled?: boolean;
    /** CSS pixel height of the drawing area. */
    height?: number;
    className?: string;
}

interface Point {
    x: number;
    y: number;
}

type Stroke = Point[];

const DEFAULT_HEIGHT = 180;
const LINE_WIDTH = 2.5;
const DOT_RADIUS = LINE_WIDTH / 2;
const FULL_CIRCLE = Math.PI * 2;
const PRIMARY_BUTTON = 0;
const FALLBACK_INK = 'black';
const PNG_MIME = 'image/png';

function getContext(canvas: HTMLCanvasElement | null): CanvasRenderingContext2D | null {
    return canvas?.getContext('2d') ?? null;
}

/**
 * Ink follows the canvas' Tailwind text colour (`text-gray-900`) so the
 * component honours the design tokens instead of a hard-coded hex.
 */
function configureInk(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement): void {
    const ink = getComputedStyle(canvas).color || FALLBACK_INK;
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = ink;
    ctx.fillStyle = ink;
}

function drawStroke(ctx: CanvasRenderingContext2D, stroke: Stroke): void {
    const first = stroke[0];
    if (!first) return;
    if (stroke.length === 1) {
        // A tap: draw a dot so a single touch is still visible.
        ctx.beginPath();
        ctx.arc(first.x, first.y, DOT_RADIUS, 0, FULL_CIRCLE);
        ctx.fill();
        return;
    }
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (const point of stroke.slice(1)) {
        ctx.lineTo(point.x, point.y);
    }
    ctx.stroke();
}

function drawSegment(ctx: CanvasRenderingContext2D, from: Point, to: Point): void {
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
}

function pointFromEvent(event: ReactPointerEvent<HTMLCanvasElement>): Point {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

export function SignaturePad({
    value = null,
    onChange,
    disabled = false,
    height = DEFAULT_HEIGHT,
    className = '',
}: SignaturePadProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [strokes, setStrokes] = useState<Stroke[]>([]);
    const [isDrawing, setIsDrawing] = useState(false);
    // The in-progress stroke lives in a ref: pointermove fires at input
    // rate and must not re-render the tree on every point.
    const activeStrokeRef = useRef<Stroke | null>(null);
    // A PNG handed back through `value` (e.g. a re-mounted step) is
    // replayed under the strokes on resize so it survives rotation.
    const restoredImageRef = useRef<HTMLImageElement | null>(null);
    // Last value we emitted, so the controlled `value` echo is not
    // mistaken for an external reset / restore.
    const lastEmittedRef = useRef<string | null>(null);

    const hasInk = strokes.length > 0 || Boolean(value);

    /** Size the backing store to the CSS box × DPR and replay the ink. */
    const redraw = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = getContext(canvas);
        if (!canvas || !ctx) return;
        const dpr = window.devicePixelRatio || 1;
        const { width, height: cssHeight } = canvas.getBoundingClientRect();
        canvas.width = Math.max(1, Math.round(width * dpr));
        canvas.height = Math.max(1, Math.round(cssHeight * dpr));
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, cssHeight);
        configureInk(ctx, canvas);
        if (restoredImageRef.current) {
            ctx.drawImage(restoredImageRef.current, 0, 0, width, cssHeight);
        }
        strokes.forEach((stroke) => drawStroke(ctx, stroke));
        if (activeStrokeRef.current) drawStroke(ctx, activeStrokeRef.current);
    }, [strokes]);

    // Initial sizing + resize / rotation safety.
    useEffect(() => {
        redraw();
        const canvas = canvasRef.current;
        if (!canvas || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => redraw());
        observer.observe(canvas);
        return () => observer.disconnect();
    }, [redraw]);

    // Controlled value: parent cleared it (kiosk auto-reset) → wipe;
    // parent supplied a PNG we did not emit → paint it back.
    useEffect(() => {
        if (value === lastEmittedRef.current) return;
        lastEmittedRef.current = value || null;
        activeStrokeRef.current = null;
        if (!value) {
            restoredImageRef.current = null;
            setStrokes([]);
            return;
        }
        const image = new Image();
        image.onload = () => {
            restoredImageRef.current = image;
            setStrokes([]);
        };
        image.src = value;
    }, [value]);

    const handlePointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
        if (disabled) return;
        if (event.pointerType === 'mouse' && event.button !== PRIMARY_BUTTON) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        const point = pointFromEvent(event);
        activeStrokeRef.current = [point];
        setIsDrawing(true);
        const ctx = getContext(event.currentTarget);
        if (!ctx) return;
        configureInk(ctx, event.currentTarget);
        drawStroke(ctx, [point]);
    };

    const handlePointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
        const stroke = activeStrokeRef.current;
        if (!stroke || disabled) return;
        const last = stroke[stroke.length - 1];
        const point = pointFromEvent(event);
        stroke.push(point);
        const ctx = getContext(event.currentTarget);
        if (!ctx || !last) return;
        drawSegment(ctx, last, point);
    };

    const finishStroke = (event: ReactPointerEvent<HTMLCanvasElement>) => {
        const stroke = activeStrokeRef.current;
        if (!stroke) return;
        activeStrokeRef.current = null;
        setIsDrawing(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        setStrokes((previous) => [...previous, stroke]);
        const dataUrl = event.currentTarget.toDataURL(PNG_MIME);
        lastEmittedRef.current = dataUrl;
        onChange(dataUrl);
    };

    const handleClear = () => {
        if (disabled) return;
        activeStrokeRef.current = null;
        restoredImageRef.current = null;
        setIsDrawing(false);
        setStrokes([]);
        const canvas = canvasRef.current;
        const ctx = getContext(canvas);
        if (canvas && ctx) {
            const { width, height: cssHeight } = canvas.getBoundingClientRect();
            ctx.clearRect(0, 0, width, cssHeight);
        }
        lastEmittedRef.current = null;
        onChange(null);
    };

    const showPlaceholder = !hasInk && !isDrawing;

    return (
        <div className={`flex flex-col gap-2 ${className}`}>
            <div
                className={`relative overflow-hidden rounded-xl border-2 border-dashed bg-white ${
                    disabled ? 'border-gray-200 opacity-60' : 'border-gray-300'
                }`}
                style={{ height }}
            >
                <canvas
                    ref={canvasRef}
                    role="img"
                    aria-label="Signature pad"
                    className={`block h-full w-full touch-none select-none text-gray-900 ${
                        disabled ? 'cursor-not-allowed' : 'cursor-crosshair'
                    }`}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={finishStroke}
                    onPointerCancel={finishStroke}
                />
                {/* Baseline the visitor signs on; purely decorative. */}
                <div className="pointer-events-none absolute inset-x-6 bottom-8 border-t border-gray-200" aria-hidden="true" />
                {showPlaceholder && (
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400">
                        Sign here
                    </span>
                )}
            </div>
            <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Sign above</span>
                <button
                    type="button"
                    onClick={handleClear}
                    disabled={disabled || !hasInk}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <Eraser size={16} />
                    Clear
                </button>
            </div>
        </div>
    );
}

export default SignaturePad;
